import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AUTH } from '@/constants/testIds';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { isNativeApp, openGoogleAuthNative } from '@/lib/native';
import { AppleSignInButton } from '@/components/AppleSignInButton';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useSEO({
    title: 'Ingresar | AutoMarket Colombia',
    description: 'Accede a tu cuenta de AutoMarket Colombia para comprar repuestos, gestionar pedidos o administrar tu tienda.',
    canonical: '/login',
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Bienvenido, ${user.name}`);
      const to = loc.state?.from || (user.role === 'admin' ? '/admin' : user.role === 'seller' ? '/vendedor' : user.role === 'logistics' ? '/logistica' : '/comprador');
      nav(to);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credenciales inválidas');
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    if (isNativeApp()) { openGoogleAuthNative(); return; }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        {/* Left illustration panel — hidden on mobile */}
        <div className="hidden lg:flex relative overflow-hidden rounded-lg bg-slate-900 text-white p-10 flex-col justify-between">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/85 to-orange-500/20" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-300">
              AutoMarket Colombia
            </div>
            <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-tighter">
              Todo para tu carro,<br />
              <span className="text-orange-400">en un solo lugar.</span>
            </h2>
            <p className="mt-4 text-sm text-slate-300 max-w-sm">
              Miles de repuestos originales y genéricos, vendedores verificados y envíos a toda Colombia.
            </p>
          </div>
          <div className="relative grid grid-cols-2 gap-3 mt-8">
            <div className="border border-white/10 bg-white/5 p-3">
              <div className="font-display text-2xl font-black text-orange-400">4.8★</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-300 mt-1">Calificación promedio</div>
            </div>
            <div className="border border-white/10 bg-white/5 p-3">
              <div className="font-display text-2xl font-black text-orange-400">100%</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-300 mt-1">Pagos seguros</div>
            </div>
          </div>
        </div>

        {/* Login form */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Ingresar</h1>
        <p className="mt-2 text-slate-600 text-sm">¿Aún no tienes cuenta? <Link to="/registro" data-testid={AUTH.switchLink} className="font-semibold text-blue-700 hover:underline">Regístrate</Link></p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</label>
            <Input
              data-testid={AUTH.emailInput}
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-none mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contraseña</label>
            <div className="relative mt-1">
              <Input
                data-testid={AUTH.passwordInput}
                type={showPassword ? 'text' : 'password'}
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none pr-10"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-900 transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-1 text-right">
              <Link
                to="/olvide-contrasena"
                data-testid="login-forgot-password-link"
                className="text-xs font-semibold text-blue-700 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </div>
          <Button
            data-testid={AUTH.submitBtn}
            type="submit"
            onClick={submit}
            disabled={loading}
            className="w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold"
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="h-px flex-1 bg-slate-200" />
          <span>O CONTINÚA CON</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Button
          data-testid={AUTH.googleBtn}
          onClick={googleLogin}
          variant="outline"
          className="w-full rounded-none border-slate-300 hover:bg-slate-50 h-11"
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 35.3 26.6 36 24 36c-5.3 0-9.7-3.1-11.3-7.4l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.4l6.2 5.2C39.8 35.4 44 30.2 44 24c0-1.3-.1-2.7-.4-3.5z"/>
          </svg>
          Continuar con Google
        </Button>
        <div className="mt-3">
          <AppleSignInButton onSuccess={(u) => nav(u.role === 'admin' ? '/admin' : u.role === 'seller' ? '/vendedor' : '/comprador')} />
        </div>
        </div>
      </div>
    </Layout>
  );
}
